import { useEffect, useRef } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { setAuthToken, setUser } from '@/utils/auth'
import { getCurrentUser } from '@/services/api/users'

/**
 * When Okta user is authenticated, sync access token and app user to localStorage
 * so the existing API client (Bearer token) works. Fetches /users/me so local user.id
 * is the database UUID (not the Okta subject).
 */
export default function OktaTokenSync() {
  const { oktaAuth, authState } = useOktaAuth()
  const syncedRef = useRef(false)

  useEffect(() => {
    if (!authState?.isAuthenticated) {
      syncedRef.current = false
      return
    }

    let cancelled = false

    const sync = async () => {
      const stored = authState.accessToken?.accessToken
      const fromManager = (await oktaAuth.tokenManager.get('accessToken')) as
        | { accessToken?: string }
        | undefined
      const token = stored ?? fromManager?.accessToken

      if (!token || cancelled) return

      setAuthToken(token)

      const claims = authState.idToken?.claims
      if (claims) {
        setUser({
          id: (claims.sub as string) || '',
          name: (claims.name as string) || (claims.email as string) || 'User',
          email: (claims.email as string) || '',
          role: (claims.role as string) || 'user',
        })
      }

      if (syncedRef.current) return
      syncedRef.current = true

      try {
        const me = await getCurrentUser()
        if (cancelled) return
        setUser({
          id: me.id,
          name: me.name,
          email: me.email,
          role: me.role,
          avatar: me.avatar ?? null,
        })
      } catch {
        syncedRef.current = false
      }
    }

    void sync()

    return () => {
      cancelled = true
    }
  }, [
    authState?.isAuthenticated,
    authState?.accessToken?.accessToken,
    authState?.idToken?.claims,
    oktaAuth,
  ])

  return null
}
